import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdPlayArrow, MdSearch, MdCheck, MdLightbulbOutline, MdExpandMore, MdFlag, MdClose,
  MdAutoAwesome, MdForum,
} from 'react-icons/md';
import api from '../../ui/api';
import PageShell, { Button } from '../../ui/PageShell';
import { useCoach } from '../help/Coach';
import { pathFor, ASK_WORDS, writtenSteps } from '../help/guides';
import { useAuth } from '../auth';
import { useNavIntent } from '../navIntent';
import { normalize } from '../search/searchEngine';
import '../help/help.css';

/**
 * «دليل الاستخدام» — every guide this role can follow: read the steps,
 * or press «ورّيني» and the system points at each button on the real
 * screen (help/Coach.js). «جهّز مدرستك» orders the setup guides the way
 * a new school is built, with a tick on each one already tried.
 */
export default function Help() {
  const {
    guides, start, done, ask,
  } = useCoach();
  const { role } = useAuth();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  const cards = useRef({});

  // Navbar search → a guide: show it here, and «ورّيني» starts it.
  useNavIntent('help', (it) => {
    if (!it.guide) return;
    setOpenId(it.guide);
    setQuery('');
    if (it.run) start(it.guide);
    else requestAnimationFrame(() => cards.current[it.guide]?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }, [start]);

  const track = pathFor(role);
  const path = useMemo(
    () => (track ? track.guides.map((id) => guides.find((x) => x.id === id)).filter(Boolean) : []),
    [track, guides],
  );
  const pathDone = path.filter((x) => done.includes(x.id)).length;
  const nextUp = path.find((x) => !done.includes(x.id));

  const shown = useMemo(() => {
    const words = normalize(query).split(' ').filter(Boolean);
    if (!words.length) return guides;
    // A guide whose title says it comes first.
    const inTitle = (x) => words.filter((w) => normalize(x.title).includes(w)).length;
    return guides
      .filter((x) => {
        const hay = normalize([x.title, x.summary, x.words, ASK_WORDS, ...writtenSteps(x).map((s) => s.title)].join(' '));
        return words.every((w) => hay.includes(w));
      })
      .sort((a, b) => inTitle(b) - inTitle(a));
  }, [guides, query]);

  return (
    <PageShell
      title="دليل الاستخدام"
      description="كيف تُنجز عملك في بوابة مشكاة، خطوة بخطوة. اضغط «ورّيني» فيدلّك النظام على كل زر في مكانه."
    >
      {path.length > 0 && (
        <section className="help-path">
          <div className="help-path-head">
            <div>
              <span className="help-path-kicker"><MdFlag size={14} /> ابدأ من هنا</span>
              <h2 className="help-path-title">{track.title}</h2>
              <p className="help-path-sub">{track.sub}</p>
            </div>
            <div className="help-meter" aria-label={`أنجزت ${pathDone} من ${path.length}`}>
              <strong>{pathDone}<small>/{path.length}</small></strong>
              <span className="help-meter-bar"><span style={{ width: `${(pathDone / path.length) * 100}%` }} /></span>
            </div>
          </div>

          <ol className="help-path-steps">
            {path.map((x, i) => {
              const isDone = done.includes(x.id);
              const isNext = nextUp?.id === x.id;
              return (
                <li key={x.id} className={`${isDone ? 'is-done' : ''}${isNext ? ' is-next' : ''}`}>
                  <button type="button" onClick={() => start(x.id)} title={`ورّيني: ${x.title}`}>
                    <span className="help-path-n">{isDone ? <MdCheck size={16} /> : i + 1}</span>
                    <span className="help-path-name">{x.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="help-path-cta">
            {nextUp ? (
              <Button onClick={() => start(nextUp.id)} data-guide="help-next">
                <MdPlayArrow size={20} /> ورّيني: {nextUp.title}
              </Button>
            ) : (
              <span className="help-path-complete"><MdCheck size={18} /> جرّبت كل خطوات «{track.title}»</span>
            )}
          </div>
        </section>
      )}

      <div className="help-search">
        <MdSearch size={20} />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ماذا تريد أن تعمل؟ مثلاً: شعبة، معلم، ابتدائي، طالب"
          aria-label="ابحث في الأدلة"
        />
        {query && (
          <button type="button" className="help-search-clear" onClick={() => setQuery('')} aria-label="مسح">
            <MdClose size={16} />
          </button>
        )}
      </div>

      <button type="button" className="help-ask" onClick={() => ask(query.trim())}>
        <MdAutoAwesome size={17} />
        <span>لم تجد ما تبحث عنه؟ <b>اسأل مشكاة</b> بكلماتك</span>
      </button>

      {shown.length === 0 ? (
        <div className="help-empty">لا يوجد دليل لـ «{query.trim()}» بعد.</div>
      ) : (
        <div className="help-grid">
          {shown.map((x) => {
            const steps = writtenSteps(x);
            const isOpen = openId === x.id;
            return (
              <article
                key={x.id}
                ref={(el) => { cards.current[x.id] = el; }}
                className={`help-card${isOpen ? ' is-open' : ''}`}
              >
                <div className="help-card-head">
                  <span className={`help-icon help-tone-${x.tone}`}><x.icon size={22} /></span>
                  <div className="help-card-titles">
                    <h3>
                      {x.title}
                      {done.includes(x.id) && <span className="help-tag"><MdCheck size={13} /> جرّبته</span>}
                    </h3>
                    <p>{x.summary}</p>
                  </div>
                </div>

                <div className="help-card-actions">
                  <Button onClick={() => start(x.id)} data-guide={`help-run-${x.id}`}>
                    <MdPlayArrow size={20} /> ورّيني
                  </Button>
                  <button
                    type="button"
                    className="help-toggle"
                    onClick={() => setOpenId(isOpen ? null : x.id)}
                    aria-expanded={isOpen}
                  >
                    الخطوات مكتوبة ({steps.length}) <MdExpandMore size={18} className="help-toggle-icon" />
                  </button>
                </div>

                {isOpen && (
                  <div className="help-detail">
                    <ol className="help-steps">
                      {steps.map((s, i) => (
                        <li key={`${x.id}-${i}`}>
                          <span className="help-step-n">{i + 1}</span>
                          <div>
                            <strong>{s.title}</strong>
                            <p>{s.text}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    {x.notes?.length > 0 && (
                      <ul className="help-notes">
                        {x.notes.map((n) => (
                          <li key={n}><MdLightbulbOutline size={17} /> <span>{n}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {role === 'admin' && <TeamQuestions guides={guides} />}
    </PageShell>
  );
}

const SOURCE = {
  ai: ['أجاب', 'success'],
  data: ['من البيانات', 'success'],
  off: ['من الأدلة', 'info'],
  limit: ['حدّ اليوم', 'warning'],
  error: ['تعذّر', 'danger'],
};

/**
 * «ما يسأل عنه الفريق» (admin): the latest questions asked to «اسأل مشكاة»
 * — a question that keeps coming back with no guide is the next guide
 * to write.
 */
function TeamQuestions({ guides }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const title = (id) => guides.find((x) => x.id === id)?.title;

  useEffect(() => {
    if (!open || rows) return;
    api.assistantLog().then(setRows).catch((e) => setError(e.message));
  }, [open, rows]);

  return (
    <section className={`help-team${open ? ' is-open' : ''}`}>
      <button type="button" className="help-team-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <MdForum size={20} />
        <span><strong>ما يسأل عنه الفريق</strong><small>آخر الأسئلة إلى «اسأل مشكاة»</small></span>
        <MdExpandMore size={20} className="help-toggle-icon" />
      </button>
      {open && (
        <div className="help-team-body">
          {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}
          {!rows && !error && <p className="help-team-muted">جارٍ التحميل…</p>}
          {rows && rows.length === 0 && <p className="help-team-muted">لم يُسأل شيء بعد.</p>}
          {rows && rows.length > 0 && (
            <ul className="help-team-list">
              {rows.map((r) => {
                const [label, tone] = SOURCE[r.source] || [r.source, 'neutral'];
                const pointed = (r.guide_ids || []).map(title).filter(Boolean);
                return (
                  <li key={r.id}>
                    <p className="help-team-q">{r.question}</p>
                    <div className="help-team-meta">
                      <span>{r.user_name || '—'}</span>
                      <span dir="ltr">{String(r.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                      <span className={`ui-badge ui-badge-${tone}`}>{label}</span>
                      {pointed.length > 0 && <span className="help-team-guides">← {pointed.join('، ')}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
